import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/api';
import toast from 'react-hot-toast';
import {
  BellIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  HomeIcon,
  InboxIcon,
  BugAntIcon,
  ViewColumnsIcon,
  FolderIcon,
  PlusCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Navbar = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isReportBugActive = location.pathname === '/bugs/new' || location.pathname.startsWith('/developer/report-bug');
  // Active state for Report Ticket (matches both tester /bugs/new and developer /developer/report-bug)
  const isReportTicketActive = isReportBugActive;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const bugCodeCache = useRef(new Map());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setSidebarCollapsed(window.innerWidth < 1024);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showNotifications || showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications, showProfileMenu]);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get('/api/notifications');
      const list = Array.isArray(data.data) ? data.data : [];
      // Deduplicate by actor+bug+type within 15 minutes
      const seen = new Map();
      const deduped = [];
      for (const n of list) {
        const actorId = n.sender?._id || n.sender || n.actorUserId?._id || n.actorUserId || n.actor?._id || n.actor?.id || '';
        const bugRaw = n.bugId ?? n.bug;
        const bugId = typeof bugRaw === 'string' ? bugRaw : (bugRaw?._id || bugRaw?.bugId || '');
        const type = n.actionType || n.type || '';
        const bucket = Math.floor(new Date(n.createdAt).getTime() / (15 * 60 * 1000));
        const key = `${actorId}|${bugId}|${type}|${bucket}`;
        if (!seen.has(key)) {
          seen.set(key, true);
          deduped.push(n);
        }
      }
      // Enrich with human Bug Code if missing
      const enriched = await Promise.all(
        deduped.map(async (n) => {
          const bugRaw = n.bugId ?? n.bug;
          let displayBugCode =
            (typeof bugRaw === 'string'
              ? (/^BUG-/i.test(bugRaw) ? bugRaw : '')
              : (bugRaw?.bugId || '')) ||
            (n.bugCode || n.code || '');
          if (!displayBugCode && typeof bugRaw === 'string') {
            if (bugCodeCache.current.has(bugRaw)) {
              displayBugCode = bugCodeCache.current.get(bugRaw);
            } else {
              try {
                const res = await axios.get(`/api/bugs/${bugRaw}`);
                const code = res.data?.data?.bugId || '';
                if (code) {
                  bugCodeCache.current.set(bugRaw, code);
                  displayBugCode = code;
                }
              } catch (_) { /* ignore lookup failures */ }
            }
          }
          // If populated bug doc lacks bugId, fetch it using _id
          if (!displayBugCode && bugRaw && typeof bugRaw === 'object' && bugRaw._id) {
            try {
              const lookupId = bugRaw._id;
              if (bugCodeCache.current.has(lookupId)) {
                displayBugCode = bugCodeCache.current.get(lookupId);
              } else {
                const res = await axios.get(`/api/bugs/${lookupId}`);
                const code = res.data?.data?.bugId || '';
                if (code) {
                  bugCodeCache.current.set(lookupId, code);
                  displayBugCode = code;
                }
              }
            } catch (_) { /* ignore lookup failures */ }
          }
          return { ...n, displayBugCode };
        })
      );

      setNotifications(enriched);
      const count = enriched.filter(n => !n.read).length;
      setUnreadCount(Math.min(99, count));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}/read`);
      // Optimistic update — no full refetch needed
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
      fetchNotifications(); // fall back to refetch on error
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/read-all');
      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all read', e);
      fetchNotifications();
    }
  };

  const getBugCode = (n) => {
    // Priority: meta.bugCode → displayBugCode → populated bugId field → raw string
    if (n.meta?.bugCode) return n.meta.bugCode;
    if (n.displayBugCode) return n.displayBugCode;
    const bugRaw = n.bugId ?? n.bug;
    if (!bugRaw) return '';
    if (typeof bugRaw === 'object') return bugRaw.bugId || '';
    return /^BUG-/i.test(bugRaw) ? bugRaw : '';
  };

  // Single source of truth for notification messages — NEVER uses raw n.message
  const buildCleanMessage = (n) => {
    const actor =
      n.sender?.name ||
      n.actor?.name ||
      (typeof n.actorName === 'string' ? n.actorName : '') ||
      'Someone';
    const type = n.type || n.actionType || '';
    const code = getBugCode(n);
    const codeTag = code ? `[${code}]` : '';
    const appName = n.applicationId?.name || '';

    // Extract status — 4-layer fallback for old + new notifications
    const extractStatus = () => {
      // 1. From structured meta (new notifications)
      if (n.meta?.status) return n.meta.status;
      // 2. Direct field
      if (n.status) return n.status;
      if (n.newStatus) return n.newStatus;
      // 3. Parse from n.title e.g. "Bug Updated: Fixed"
      if (typeof n.title === 'string') {
        const m = n.title.match(/Bug Updated:\s*(.+)/i);
        if (m?.[1]) return m[1].trim();
      }
      // 4. Parse from n.message e.g. "Bug "X" updated to Fixed by ..."
      if (typeof n.message === 'string') {
        const m = n.message.match(/updated?\s+to\s+([A-Za-z ]+?)\s+by/i) ||
          n.message.match(/status.*?:\s*([A-Za-z ]+)/i);
        if (m?.[1]) return m[1].trim();
      }
      return '';
    };
    const status = extractStatus();

    switch (type) {
      case 'bug_assigned':
        return `${actor} assigned ${codeTag} to you`;
      case 'bug_reported':
        return `${actor} reported ${codeTag}${appName ? ` in ${appName}` : ''}`;
      case 'bug_fixed':
        return `${actor} fixed ${codeTag}`;
      case 'bug_reopened':
        return `${actor} reopened ${codeTag}`;
      case 'mention':
        return `${actor} mentioned you in ${codeTag}`;
      case 'status_changed':
      case 'bug_status_change':
        return status
          ? `${actor} changed ${codeTag} to ${status}`
          : `${actor} updated ${codeTag}`;
      case 'team_assignment':
      case 'team_member_added':
        return `You were added to ${appName || 'an application'}`;
      case 'ownership_transfer':
        return `${actor} transferred ownership to you`;
      case 'comment':
        return `${actor} commented on ${codeTag}`;
      default:
        // Never show n.message (could be raw bug description)
        return `${actor} updated ${codeTag || 'a bug'}`;
    }
  };

  // Mention subtext — max 80 chars, strip HTML tags
  const getMentionSnippet = (n) => {
    if ((n.type || n.actionType) !== 'mention') return null;
    const raw = n.meta?.snippet || n.meta?.text || '';
    const stripped = raw.replace(/<[^>]*>/g, '').replace(/@\[[^\]]+\]/g, '').trim();
    return stripped.length > 80 ? stripped.slice(0, 77) + '…' : stripped || null;
  };



  const handleLogout = () => {
    logout();
    toast.dismiss(); // clear any lingering toasts before showing logout message
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (!user) return null;

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-[72px]' : 'w-[220px]'} bg-[#1E293B] text-gray-200 flex flex-col sticky top-0 h-screen border-r border-white/5 transition-[width] duration-200`}
      >
        <div className={`${sidebarCollapsed ? 'px-3' : 'px-5'} h-16 flex items-center justify-between border-b border-white/5`}
        >
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            <img src="/assets/logo-final.jpg" alt="Bug Tracker" className="h-8 w-8 rounded-full" />
            {!sidebarCollapsed && (
              <div className="flex flex-col min-w-0">
                <span className="text-[15px] font-semibold text-[#E2E8F0] truncate">Bug Tracker</span>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(v => !v)}
            className="hidden lg:inline-flex h-8 w-8 items-center justify-center rounded hover:bg-[#334155] transition-colors"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            <ChevronDownIcon className={`h-4 w-4 text-[#CBD5E1] transition-transform ${sidebarCollapsed ? '-rotate-90' : 'rotate-90'}`} />
          </button>
        </div>

        <nav className="py-3 flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Overview Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-2 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Overview</h3>
              </div>
            )}

            <NavLink to="/dashboard" end>
              {({ isActive }) => (
                <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                  <HomeIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                  {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Dashboard</span>}
                </div>
              )}
            </NavLink>

            {user.role === 'developer' && (
              <NavLink to="/applications" end>
                {({ isActive }) => (
                  <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                    <FolderIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                    {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Applications</span>}
                  </div>
                )}
              </NavLink>
            )}

            {user.role === 'tester' && (
              <NavLink to="/tester/applications" end>
                {({ isActive }) => (
                  <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                    <FolderIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                    {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Applications</span>}
                  </div>
                )}
              </NavLink>
            )}

            {/* Work Items Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Work Items</h3>
              </div>
            )}

            <NavLink to="/bugs" end>
              {({ isActive }) => (
                <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                  <BugAntIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                  {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Bugs</span>}
                </div>
              )}
            </NavLink>

            <NavLink to="/kanban" end>
              {({ isActive }) => (
                <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                  <ViewColumnsIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                  {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Kanban Board</span>}
                </div>
              )}
            </NavLink>

            {(user.role === 'developer' || user.role === 'tester') && (
              <NavLink to="/view-timeline" end>
                {({ isActive }) => (
                  <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                    <ClockIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                    {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Timeline</span>}
                  </div>
                )}
              </NavLink>
            )}



            {user.role === 'developer' && (
              <button
                onClick={() => navigate('/developer/report-bug')}
                className={`mx-2 my-1 flex w-[calc(100%-1rem)] items-center gap-3 rounded-md px-3 py-2 text-[14px] transition-colors ${isReportBugActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}
                type="button"
              >
                <PlusCircleIcon className={`h-5 w-5 flex-shrink-0 ${isReportTicketActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={isReportTicketActive ? 'font-semibold' : 'font-medium'}>Report Ticket</span>}
              </button>
            )}

            {user.role === 'tester' && (
              <button
                onClick={() => navigate('/bugs/new')}
                className={`mx-2 my-1 flex w-[calc(100%-1rem)] items-center gap-3 rounded-md px-3 py-2 text-[14px] transition-colors ${isReportBugActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}
                type="button"
              >
                <PlusCircleIcon className={`h-5 w-5 flex-shrink-0 ${isReportTicketActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={isReportTicketActive ? 'font-semibold' : 'font-medium'}>Report Ticket</span>}
              </button>
            )}

            {/* Communication Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Communication</h3>
              </div>
            )}

            <NavLink
              to="/inbox"
              end
              className={({ isActive }) =>
                `mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'
                } ${sidebarCollapsed ? 'justify-center' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <InboxIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                  {!sidebarCollapsed && <span className={`${isActive ? 'font-semibold' : 'font-medium'}`}>Discussion & Notifications</span>}
                </>
              )}
            </NavLink>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top Header: Logo + Notifications + Profile */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="h-16 px-4 sm:px-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to="/dashboard" className="flex items-center gap-2">
                <img src="/assets/logo-final.jpg" alt="Bug Tracker" className="h-8 w-8 rounded-full" />
                <span className="text-[15px] font-semibold text-gray-900">Bug Tracker</span>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => {
                    const next = !showNotifications;
                    setShowNotifications(next);
                    if (next) {
                      // Immediately clear the badge on first click
                      setUnreadCount(0);
                      fetchNotifications();
                      markAllAsRead();
                    }
                  }}
                  className="relative p-2 text-gray-600 hover:text-primary-600 focus:outline-none"
                >
                  <BellIcon className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center h-4 w-4 text-[10px] font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full ring-2 ring-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="absolute right-0 top-full mt-2 flex flex-col bg-white rounded-2xl border border-gray-100 z-[9999] overflow-hidden"
                    style={{
                      width: '370px',
                      maxHeight: '65vh',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                      animation: 'notifDropIn 0.18s cubic-bezier(.16,.84,.44,1) both',
                    }}
                  >
                    {/* ── Header ── */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-bold text-gray-900 tracking-tight">Notifications</span>
                        {unreadCount > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded-full leading-none">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={async (e) => { e.stopPropagation(); await markAllAsRead(); }}
                          disabled={unreadCount === 0}
                          className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${unreadCount > 0
                            ? 'text-indigo-600 hover:bg-indigo-50 cursor-pointer'
                            : 'text-gray-300 cursor-not-allowed'
                            }`}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /></svg>
                          Mark all read
                        </button>
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>

                    {/* ── Scrollable List ── */}
                    <div className="flex-1 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin' }}>
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <BellIcon className="h-6 w-6 text-gray-300" />
                          </div>
                          <p className="text-[13px] font-semibold text-gray-700">All caught up!</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">No new notifications</p>
                        </div>
                      ) : (() => {
                        // Group by day
                        const TYPE_EMOJI = { bug_assigned: '📋', status_changed: '🔄', bug_status_change: '🔄', bug_fixed: '✅', bug_reported: '🐛', bug_reopened: '📂', mention: '@', team_assignment: '👥', team_member_added: '👥', ownership_transfer: '👑', comment: '💬' };
                        const TYPE_BG = { bug_assigned: 'bg-indigo-100', status_changed: 'bg-amber-100', bug_status_change: 'bg-amber-100', bug_fixed: 'bg-green-100', bug_reported: 'bg-red-100', bug_reopened: 'bg-orange-100', mention: 'bg-purple-100', team_assignment: 'bg-blue-100', team_member_added: 'bg-blue-100', ownership_transfer: 'bg-teal-100', comment: 'bg-sky-100' };
                        const AVATAR_COLORS = ['bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-teal-500', 'bg-cyan-500', 'bg-blue-500'];
                        const aColor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
                        const dayLabel = (date) => {
                          const d = new Date(date), today = new Date();
                          const diff = Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
                          if (diff === 0) return 'Today'; if (diff === 1) return 'Yesterday'; if (diff < 7) return 'Earlier this week'; return 'Older';
                        };
                        const buildMsg = buildCleanMessage;
                        const formatFullDateTime = (dateString) => {
                          const d = new Date(dateString);
                          if (isNaN(d.getTime())) return '';
                          return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}, ${d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
                        };
                        const grouped = {};
                        for (const n of notifications) { const g = dayLabel(n.createdAt); (grouped[g] = grouped[g] || []).push(n); }
                        const ORDER = ['Today', 'Yesterday', 'Earlier this week', 'Older'];
                        return ORDER.filter(g => grouped[g]).map(group => (
                          <div key={group}>
                            <div className="sticky top-0 z-10 px-4 py-1.5 bg-gray-50 border-y border-gray-100/80">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{group}</span>
                            </div>
                            {grouped[group].map(n => {
                              const actor = n.sender?.name || n.actor?.name || (typeof n.actorName === 'string' ? n.actorName : '') || 'Someone';
                              const initial = actor.charAt(0).toUpperCase();
                              const emoji = TYPE_EMOJI[n.type] || '🔔';
                              const badgeBg = TYPE_BG[n.type] || 'bg-gray-100';
                              const msg = buildMsg(n);
                              // Use clean structured title, never raw populated doc
                              const bugTitle = n.meta?.bugTitle || (typeof n.bugId === 'object' ? n.bugId?.title : '') || (typeof n.bug === 'object' ? n.bug?.title : '');
                              const mentionSnippet = getMentionSnippet(n);
                              const appName = n.applicationId?.name || '';
                              const time = formatFullDateTime(n.createdAt);
                              const bugRaw = n.bugId ?? n.bug;
                              const bugId = bugRaw?._id || (typeof bugRaw === 'string' ? bugRaw : null);
                              return (
                                <div
                                  key={n._id}
                                  className={`relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 group ${!n.read ? 'bg-indigo-50/50 hover:bg-indigo-50' : 'bg-white hover:bg-gray-50/80'}`}
                                  onClick={async () => {
                                    if (!n.read) await markAsRead(n._id);
                                    if (bugId) { const qs = n.commentId ? `?commentId=${n.commentId}` : ''; navigate(`/bugs/${bugId}${qs}`); }
                                    else if (n.applicationId) { const id = n.applicationId._id || n.applicationId; navigate(`/applications/${id}/timeline`); }
                                    setShowNotifications(false);
                                  }}
                                >
                                  {/* Unread accent bar */}
                                  {!n.read && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-8 rounded-r-full bg-indigo-500" />}

                                  {/* Avatar + type badge */}
                                  <div className="relative shrink-0 mt-0.5">
                                    <div className={`w-9 h-9 rounded-full ${aColor(actor)} flex items-center justify-center text-white text-[13px] font-bold`}>{initial}</div>
                                    <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${badgeBg} flex items-center justify-center text-[9px] ring-2 ring-white`}>
                                      <span role="img" aria-hidden="true">{emoji}</span>
                                    </div>
                                  </div>

                                  {/* Text content */}
                                  <div className="flex-1 min-w-0 pr-1">
                                    <p className={`text-[12.5px] leading-snug ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{msg}</p>
                                    {/* Mention subtext (1 line, stripped, max 80 chars) */}
                                    {mentionSnippet && (
                                      <p className="text-[11px] text-gray-500 italic leading-tight truncate mt-0.5">"{mentionSnippet}"</p>
                                    )}
                                    {/* Bug title subtext for non‑mention types */}
                                    {!mentionSnippet && bugTitle && (
                                      <p className="text-[11px] text-gray-400 leading-tight truncate mt-0.5">{bugTitle}</p>
                                    )}
                                    <p className="flex items-center gap-1 text-[10.5px] text-gray-400 mt-0.5">
                                      {appName && <><span className="truncate max-w-[120px]">{appName}</span><span className="text-gray-300">•</span></>}
                                      <span className="shrink-0">{time}</span>
                                    </p>
                                  </div>



                                  {/* Hover quick actions */}
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-white/95 rounded-lg shadow-sm border border-gray-100 px-1 py-0.5" onClick={e => e.stopPropagation()}>
                                    {!n.read && (
                                      <button onClick={async (e) => { e.stopPropagation(); await markAsRead(n._id); }} title="Mark as read" className="p-1 text-gray-400 hover:text-indigo-600 rounded transition-colors">
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /></svg>
                                      </button>
                                    )}
                                    <button onClick={async (e) => { e.stopPropagation(); try { await axios.delete(`/api/notifications/${n._id}`); fetchNotifications(); } catch (err) { console.error(err); } }} title="Delete" className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors">
                                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ));
                      })()}
                    </div>

                    {/* ── Footer ── */}
                    {notifications.length > 0 && (
                      <div className="shrink-0 px-4 py-2.5 bg-white border-t border-gray-100">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate('/inbox'); setShowNotifications(false); }}
                          className="w-full py-2 text-[12px] font-semibold text-indigo-600 hover:text-white hover:bg-indigo-600 bg-indigo-50 rounded-xl transition-all flex items-center justify-center gap-1.5"
                        >
                          View all notifications
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                        </button>
                      </div>
                    )}

                    <style>{`
                      @keyframes notifDropIn {
                        from { opacity:0; transform:translateY(-8px) scale(0.96); }
                        to   { opacity:1; transform:translateY(0) scale(1); }
                      }
                    `}</style>
                  </div>
                )}
              </div>

              {/* User Profile Menu */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <UserCircleIcon className="h-8 w-8 text-gray-600" />
                  <div className="text-sm text-left hidden md:block">
                    <p className="font-semibold text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <p className="text-base font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                      <p className="text-xs text-gray-400 mt-1">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/account-settings');
                        setShowProfileMenu(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100"
                    >
                      <Cog6ToothIcon className="h-5 w-5" />
                      <span className="font-medium">Account Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Navbar;
